import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Erreur chargement utilisateurs:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleUserStatus = async (id, currentStatus) => {
    try {
      await api.put(`/admin/users/${id}`, { 
        isActive: !currentStatus 
      });
      fetchUsers();
    } catch (error) {
      console.error('Erreur mise à jour utilisateur:', error);
      alert('Erreur lors de la mise à jour');
    }
  };

  const deleteUser = async (id) => {
    try {
      await api.delete(`/admin/users/${id}`);
      fetchUsers();
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error('Erreur suppression utilisateur:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const getRoleBadge = (role) => {
    const badges = {
      admin: { label: 'Administrateur', color: 'bg-purple-100 text-purple-600' },
      manager: { label: 'Gestionnaire', color: 'bg-blue-100 text-blue-600' },
      delivery: { label: 'Livreur', color: 'bg-orange-100 text-orange-600' },
      user: { label: 'Client', color: 'bg-gray-100 text-gray-600' }
    };
    const b = badges[role] || badges.user;
    return <span className={`px-2 py-1 rounded-full text-xs ${b.color}`}>{b.label}</span>;
  };

  const getStatusBadge = (status) => {
    return status === true || status === 'true' 
      ? <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-600">Actif</span>
      : <span className="px-2 py-1 rounded-full text-xs bg-red-100 text-red-600">Inactif</span>;
  };

  const filteredUsers = users.filter(user => {
    const matchRole = roleFilter === 'all' || user.role === roleFilter;
    const matchSearch = `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        user.email.toLowerCase().includes(searchTerm.toLowerCase());
    return matchRole && matchSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-500">Chargement des utilisateurs...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Gestion des utilisateurs</h1>
          <p className="text-gray-500 mt-1">Consultez et gérez les comptes clients et gestionnaires</p>
        </div>
      </div>

      {/* Filtres et recherche */}
      <div className="bg-white rounded-xl shadow-md p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-center justify-between">
          <div className="flex gap-2">
            <button 
              onClick={() => setRoleFilter('all')} 
              className={`px-4 py-2 rounded-lg transition ${roleFilter === 'all' ? 'bg-primary text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
            >
              Tous
            </button>
            <button 
              onClick={() => setRoleFilter('user')} 
              className={`px-4 py-2 rounded-lg transition ${roleFilter === 'user' ? 'bg-primary text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
            >
              Clients
            </button>
            <button 
              onClick={() => setRoleFilter('admin')} 
              className={`px-4 py-2 rounded-lg transition ${roleFilter === 'admin' ? 'bg-primary text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
            >
              Administrateurs
            </button>
            <button 
              onClick={() => setRoleFilter('delivery')} 
              className={`px-4 py-2 rounded-lg transition ${roleFilter === 'delivery' ? 'bg-primary text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
            >
              Livreurs
            </button>
          </div>
          <div className="relative">
            <input 
              type="text" 
              placeholder="Rechercher..." 
              value={searchTerm} 
              onChange={(e) => setSearchTerm(e.target.value)} 
              className="pl-10 pr-4 py-2 border rounded-lg w-64 focus:outline-none focus:border-secondary" 
            />
            <svg className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
      </div>

      {/* Tableau des utilisateurs */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold">Nom</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Email</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Téléphone</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Rôle</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Statut</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Date</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                    Aucun utilisateur trouvé
                  </td>
                </tr>
              ) : (
                filteredUsers.map(user => (
                  <tr key={user.id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium">{user.first_name} {user.last_name}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">{user.email}</td>
                    <td className="px-4 py-3">{user.phone || '-'}</td>
                    <td className="px-4 py-3">{getRoleBadge(user.role)}</td>
                    <td className="px-4 py-3">{getStatusBadge(user.is_active)}</td>
                    <td className="px-4 py-3 text-sm">{new Date(user.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button 
                          onClick={() => { setSelectedUser(user); setShowUserModal(true); }} 
                          className="text-secondary hover:underline text-sm"
                        >
                          Voir
                        </button>
                        <button 
                          onClick={() => toggleUserStatus(user.id, user.is_active)} 
                          className={`text-sm ${user.is_active ? 'text-red-600 hover:underline' : 'text-green-600 hover:underline'}`}
                        >
                          {user.is_active ? 'Désactiver' : 'Activer'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Détails utilisateur */}
      {showUserModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-full max-w-2xl p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold">Profil utilisateur</h2>
              <button 
                onClick={() => { setShowUserModal(false); setSelectedUser(null); }} 
                className="text-gray-500 text-2xl"
              >
                ✕
              </button>
            </div>
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-semibold">Informations personnelles</p>
                <p className="mt-2">{selectedUser.first_name} {selectedUser.last_name}</p>
                <p>{selectedUser.email}</p>
                <p>{selectedUser.phone || 'Non renseigné'}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-semibold">Compte</p>
                <p className="mt-2">Rôle : {selectedUser.role}</p>
                <p>Statut : {selectedUser.is_active ? 'Actif' : 'Inactif'}</p>
                <p>Inscrit le : {new Date(selectedUser.created_at).toLocaleDateString()}</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button 
                onClick={() => toggleUserStatus(selectedUser.id, selectedUser.is_active)} 
                className={`flex-1 py-2 rounded-lg ${selectedUser.is_active ? 'bg-red-500 text-white' : 'bg-green-500 text-white'}`}
              >
                {selectedUser.is_active ? 'Désactiver le compte' : 'Activer le compte'}
              </button>
              <button 
                onClick={() => setShowUserModal(false)} 
                className="flex-1 bg-gray-200 py-2 rounded-lg"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal confirmation suppression */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold mb-4">Confirmer la suppression</h2>
            <p className="text-gray-600 mb-6">Êtes-vous sûr de vouloir supprimer cet utilisateur ? Cette action est irréversible.</p>
            <div className="flex gap-3">
              <button onClick={() => deleteUser(showDeleteConfirm)} className="flex-1 bg-red-600 text-white py-2 rounded-lg">Supprimer</button>
              <button onClick={() => setShowDeleteConfirm(null)} className="flex-1 bg-gray-200 py-2 rounded-lg">Annuler</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;